import Control from "sap/ui/core/Control";
import type { MetadataOptions } from "sap/ui/core/Element";
import RenderManager from "sap/ui/core/RenderManager";
import Item from "sap/ui/core/Item";
import OverflowToolbar from "sap/m/OverflowToolbar";
import Title from "sap/m/Title";
import ToolbarSpacer from "sap/m/ToolbarSpacer";
import Select from "sap/m/Select";
import Link from "sap/m/Link";
import Column from "sap/m/Column";
import GroupHeaderListItem from "sap/m/GroupHeaderListItem";
import { ListBase$SelectionChangeEvent, ListBase$UpdateFinishedEvent } from "sap/m/ListBase";
import ActionsPlaceholder from "sap/m/upload/ActionsPlaceholder";
import { UploadSetwithTable$BeforeUploadStartsEvent } from "sap/m/plugins/UploadSetwithTable";
import JSONModel from "sap/ui/model/json/JSONModel";
import Sorter from "sap/ui/model/Sorter";
import Filter from "sap/ui/model/Filter";
import FilterOperator from "sap/ui/model/FilterOperator";
import Context from "sap/ui/model/odata/v4/Context";
import ODataListBinding from "sap/ui/model/odata/v4/ODataListBinding";
import UploadTable from "ips/ac/control/FileUpload/UploadTable";
import TableRow from "ips/ac/control/FileUpload/TableRow";
import Util from "ips/ac/util/Util";
import HelperMethods from "ips/ac/controller/scfld/mixin/HelperMethods";
import FilterUtil from "ips/ac/util/FilterUtil";
import MetadataUtil from "ips/ac/util/MetadataUtil";
import File from "ips/ac/definition/odata/File";
import FileMapping from "ips/ac/definition/odata/FileMapping";
import OdataParameter from "ips/ac/definition/odata/Parameter";

/** entry of the SmartValueHelp (SVH) model */
interface ValueHelpEntry {
  Id: string;
  Value: string;
  TextShort: string;
}

/** group of the items binding, see Sorter with group: true */
interface ItemGroup {
  key?: string;
}

/** the event parameters of beforeUploadStarts are extended by the temporary row data (read by UploadTable) */
type BeforeUploadStartsEvent = UploadSetwithTable$BeforeUploadStartsEvent & {
  mParameters: Record<string, unknown>;
};

const helperMethods = new HelperMethods();

/**
 * @namespace ips.ac.control.FileUpload
 */
export default class DefaultFileDetails extends Control {
  static readonly metadata: MetadataOptions = {
    properties: {
      entityType: { type: "string" },
      displayCategoryIds: { type: "string[]" },
      uploadCategoryId: { type: "string" },
      richText: { type: "boolean", defaultValue: false },
      /**
       * Number of displayed files as "(n)", updated after each table update.
       * Bind it two-way (e.g. to "ui>/AttachmentCount") to keep a section title in sync after upload/delete.
       */
      countText: { type: "string", defaultValue: "" },
      fileTypes: {
        type: "string[]", defaultValue: ["png", "jpeg", "jpg", "pdf", "msg", "eml"],
      },
      mediaTypes: {
        type: "string[]",
        defaultValue: ["image/png", "image/jpeg", "application/pdf", "application/octet-stream", "", "message/rfc822"],
      },
    },

    aggregations: {
      _table: { type: "ips.ac.control.FileUpload.UploadTable", multiple: false },
    },
  };

  static renderer = function(rm: RenderManager, oControl: DefaultFileDetails): void {
    rm.renderControl(oControl._table);
  };

  // set in init(), so no initializers here (they would run after init and overwrite the values)
  private _jsonModel: JSONModel;
  private _toolBar: OverflowToolbar;
  private _select: Select;
  private _link: Link;
  private _actionsPlaceholder: ActionsPlaceholder;
  private _table: UploadTable;

  /**
   * OData $filter expression for the files displayed by the control, relative to a FileDetails navigation property
   * (e.g. for "Orders('...')/FileDetails/$count"). Must match the category/rich text filter of _bindItems.
   * @param {string[]} aDisplayCategoryIds - same values as the displayCategoryIds property
   * @param {boolean} [bRichText=false] - same value as the richText property
   * @returns {string} the $filter expression
   * @public
   * @static
   */
  public static getCountFilter(aDisplayCategoryIds: string[], bRichText = false): string {
    const sCategoryIdFilter = aDisplayCategoryIds.map(sId => `Category_Id eq '${sId}'`).join(" or ");
    return `Category_Value ${bRichText ? "eq" : "ne"} 'RICHTEXT' and (${sCategoryIdFilter})`;
  }

  public init(): void {
    this._jsonModel = new JSONModel({});
    this.setModel(this._jsonModel, "internal");

    this._select = new Select({ selectedKey: "OTHERS" });
    this._link = new Link({
      enabled: false, text: "{i18n>DownloadAttachment}", press: () => this.onDownloadFiles(),
    });
    this._actionsPlaceholder = new ActionsPlaceholder({
      placeholderFor: "UploadButtonPlaceholder",
    });
    this._toolBar = new OverflowToolbar({
      content: [new Title({ text: "{i18n>@OrderFileDetails} ({internal>/count})" }), new ToolbarSpacer(), this._select, this._link, this._actionsPlaceholder],
    });

    this._table = new UploadTable({
      headerToolbar: this._toolBar,
      columns: [new Column()],
      uploadProvider: (oEvent) => this.prepareUpload(oEvent as BeforeUploadStartsEvent),
    });

    this.setAggregation("_table", this._table);
    this._table.attachSelectionChange(this.onSelectionChange, this);
    this._table.attachUpdateFinished(this.onUpdateFinished, this);
    this._table.setFileTypes(this.getFileTypes());
    this._table.setMediaTypes(this.getFileTypes());

    this.attachModelContextChange(this._onModelContextChange, this);
  }

  private _getTable(): UploadTable {
    return this._table;
  }

  private _getSelect(): Select {
    return this._select;
  }

  public setDisplayCategoryIds(aDisplayCategoryIds: string[]): this {
    this.setProperty("displayCategoryIds", aDisplayCategoryIds, true);
    this._applyBindings();
    return this;
  }

  public setRichText(isRichText: boolean): this {
    this.setProperty("richText", isRichText, true);
    this._getSelect().setSelectedKey("RICHTEXT");
    this._getSelect().setVisible(!isRichText);
    return this;
  }

  private _onModelContextChange(): void {
    this._applyBindings();
  }

  private _applyBindings(): void {
    const oCtx = this.getBindingContext();
    if (!oCtx) {
      return;
    }
    void this._bindItems();
    this._bindCategoryItems();
  }

  private async _bindItems(): Promise<void> {
    const oCtx = this.getBindingContext() as Context;
    await oCtx.requestObject();

    const sPath = oCtx.getPath();
    const sEntitySet = MetadataUtil.getEntitySetByPath(sPath);
    const oConstraint = MetadataUtil.getReferentialConstraint("/" + sEntitySet, FileMapping.DETAIL);

    const sParameterName = Object.values(oConstraint)[0];
    let sGuidParamName = Object.keys(oConstraint)[0];
    sGuidParamName = sGuidParamName !== "Sid" ? sGuidParamName : OdataParameter.GUID;

    const sValueHelpGuid = oCtx.getProperty(sGuidParamName) as string;

    const aCategoryIdFilters = this.getDisplayCategoryIds().map(sId => new Filter({
      path: "Category_Id",
      operator: FilterOperator.EQ,
      value1: sId,
    }));
    const richText = this.getRichText();
    const aFilters = [
      FilterUtil.getAnyFilterEq(FileMapping.DETAIL, sParameterName, sValueHelpGuid),
      new Filter({
        filters: [
          new Filter({
            path: "Category_Value",
            operator: richText ? FilterOperator.EQ : FilterOperator.NE,
            value1: "RICHTEXT",
          }),
          new Filter({ filters: aCategoryIdFilters, and: false })],
        and: true,
      }),
    ];

    this._getTable().bindAggregation("items", {
      path: "/" + File.ENTITY_SET,
      filters: aFilters,
      sorter: [new Sorter({
        path: OdataParameter.CATEGORY_VALUE, group: true,
      }), new Sorter({ path: OdataParameter.UPDATED_ON, descending: true }),
      ],
      template: new TableRow(),
      groupHeaderFactory: (oGroup: ItemGroup) => this._groupHeaderFactory(oGroup),
    });
  }

  private _groupHeaderFactory(oGroup: ItemGroup): GroupHeaderListItem {
    const sValue = (oGroup && oGroup.key) ? oGroup.key : "???";

    const aItems = (Util.getModel("SVH").getProperty("/") as ValueHelpEntry[]).filter(function(oItem) {
      return oItem.Id.startsWith("FILE_CATEGORY") && oItem.Value === sValue;
    });

    return new GroupHeaderListItem({
      title: (aItems.length > 0) ? aItems[0].TextShort : sValue,
      upperCase: false,
    });
  }

  private _bindCategoryItems(): void {
    const sCategoryId = this.getUploadCategoryId();
    const richText = this.getRichText();

    this._getSelect().bindAggregation("items", {
      path: "SVH>/",
      filters: [new Filter({
        filters: [
          new Filter({
            path: "Value",
            operator: richText ? FilterOperator.EQ : FilterOperator.NE,
            value1: "RICHTEXT",
          }),
          new Filter({ path: "Id", operator: FilterOperator.EQ, value1: sCategoryId })], and: true,
      })],
      sorter: [new Sorter({ path: "TextShort" })],
      template: new Item({ key: "{SVH>Value}", text: "{SVH>TextShort}" }),
    });
  }


  public getActionPlaceHolder(): ActionsPlaceholder {
    return this._actionsPlaceholder;
  }

  public onDownloadFiles(): void {
    const table = this._getTable();

    table.getSelectedItems().forEach(oItem => {
      const context = oItem.getBindingContext();
      table.getUploadSet().download(context, true);
    });
  }

  public prepareUpload(oEvent: BeforeUploadStartsEvent): void {
    const item = oEvent.getParameter("item");
    const oContext = this.getBindingContext();
    const sEntityType = this.getEntityType();
    const sCategory = this._getSelect().getSelectedKey();
    const sCategoryId = this.getUploadCategoryId();
    let sInstanceGuid = oContext.getProperty((sEntityType === "User") ? "Email" : "Guid") as string;
    if (!sInstanceGuid) {
      // can't get property on context for FileDetailsRte
      sInstanceGuid = Util.findParentView(this).getBindingContext().getProperty("Guid") as string;
    }
    const sFileName = item.getFileName();

    const parameters = [
      { key: "Instance-Type", value: helperMethods.stringToUpperSnakeCase(sEntityType) },
      { key: "Instance-Guid", value: sInstanceGuid },
      { key: "Category", value: sCategory },
      { key: "Name", value: encodeURIComponent(sFileName) },
      { key: "Category-Id", value: sCategoryId },
    ];
    parameters.forEach(parameter => {
      item.addHeaderField(new Item({
        key: parameter.key, text: parameter.value,
      }));
    });

    oEvent.mParameters.tempData = {
      Name: item.getFileName(),
      MimeType: item.getMediaType(),
      CreatedBy_UserFullname: Util.getComponent().getUser().getFullName(),
      CreatedOn: new Date(),
      UpdatedOn: new Date(),
      FileSize: item.getFileSize(),
      Type_Value: "", // aus Endung der Datei ableiten
      Category_Value: sCategory,
      Category_Id: sCategoryId,
      UploadStatus: "Uploading",
    };
  }

  public setFileTypes(value: string[]): this {
    this.setProperty("fileTypes", value, true);
    this._getTable().setFileTypes(value);

    return this;
  }

  public setMediaTypes(value: string[]): this {
    this.setProperty("mediaTypes", value, true);
    this._getTable().setMediaTypes(value);

    return this;
  }

  public onSelectionChange(oEvent: ListBase$SelectionChangeEvent): void {
    const hasSelection = (oEvent.getSource() as UploadTable).getSelectedItems().length > 0;
    this._link.setEnabled(hasSelection);
  }

  public setCountText(sCountText: string): this {
    // only used for bindings outside of the control, no re-rendering needed
    return this.setProperty("countText", sCountText, true);
  }

  public onUpdateFinished(oEvent: ListBase$UpdateFinishedEvent): void {
    const table = oEvent.getSource() as UploadTable;
    const iCount = (table.getBinding("items") as ODataListBinding).getContexts().length;
    this._jsonModel.setProperty("/count", iCount);
    this.setCountText("(" + iCount + ")");
  }
}
